import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

import Select from './Select';
import Button from './Button';
import { Checkbox } from './Checkbox';

const SearchFilterPanel = ({ 
  onSearch,
  onFilterChange,
  filters = {},
  searchPlaceholder = 'Search...',
  showAdvancedFilters = true,
  className = ''
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    classroom: '',
    subject: '',
    dateRange: '',
    status: [],
  });

  const classroomOptions = [
    { value: '', label: 'All Classrooms' },
    { value: 'class-a', label: 'Class A' },
    { value: 'class-b', label: 'Class B' },
    { value: 'class-c', label: 'Class C' },
  ];

  const subjectOptions = [
    { value: '', label: 'All Subjects' },
    { value: 'math', label: 'Mathematics' },
    { value: 'science', label: 'Science' },
    { value: 'english', label: 'English' },
    { value: 'history', label: 'History' },
  ];

  const dateRangeOptions = [
    { value: '', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' },
  ];

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (onSearch) {
        onSearch(searchQuery);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, onSearch]);

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(selectedFilters);
    }
  }, [selectedFilters, onFilterChange]);

  const handleFilterChange = (filterName, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleStatusToggle = (status) => {
    setSelectedFilters(prev => ({
      ...prev,
      status: prev?.status?.includes(status)
        ? prev?.status?.filter(s => s !== status)
        : [...prev?.status, status],
    }));
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedFilters({
      classroom: '',
      subject: '',
      dateRange: '',
      status: [],
    });
  };

  const activeFilterCount = Object.values(selectedFilters)?.filter(value => 
    Array.isArray(value) ? value?.length > 0 : value !== ''
  )?.length;

  return (
    <div className={`bg-card rounded-lg shadow-sm ${className}`}>
      <div className="p-4">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <Icon 
              name="Search" 
              size={20} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 focus:ring-offset-background transition-organic"
            />
          </div>

          {showAdvancedFilters && (
            <Button
              variant="outline"
              iconName="SlidersHorizontal"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          )}
        </div>

        {isExpanded && showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t border-border space-y-4 stagger-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Classroom"
                options={classroomOptions}
                value={selectedFilters?.classroom}
                onChange={(value) => handleFilterChange('classroom', value)}
              />

              <Select
                label="Subject"
                options={subjectOptions}
                value={selectedFilters?.subject}
                onChange={(value) => handleFilterChange('subject', value)}
              />

              <Select
                label="Date Range"
                options={dateRangeOptions}
                value={selectedFilters?.dateRange}
                onChange={(value) => handleFilterChange('dateRange', value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <div className="flex flex-wrap gap-3">
                {statusOptions?.map((status) => (
                  <Checkbox
                    key={status?.value}
                    label={status?.label}
                    checked={selectedFilters?.status?.includes(status?.value)}
                    onChange={() => handleStatusToggle(status?.value)}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <Button variant="outline" onClick={handleClearFilters}>
                Clear All
              </Button>
              <Button onClick={() => setIsExpanded(false)}>
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchFilterPanel;